import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { badNameChars, NAME_CHARS } from "./subscriberCreate";
import type { SearchAddress, SubscriberMatch } from "./subscriberSearch";
import { isMobile10 } from "../domain/testMobile";

/**
 * Users › Customer lookup › Edit profile (owner 2026-10-18; radius-wiki
 * subscriber-cart-v1 §13.2): everything but the primary mobile.
 *
 *   PUT /access/operator/{ns}/subscribers/{AccountID}/profile   subscriberprofile:write
 *
 * radaccess v1.123 (radcustomer v0.2.51, radbilling v0.1.102). The body names
 * only what changed; the server refuses the mobile outright.
 */
export interface ProfileForm {
  name: string;
  gender: "" | "male" | "female";
  email: string;
  alternate_mobile: string;
  /** "" keeps the recorded type. */
  idprooftype: string;
  /** "" keeps the recorded number, which is never shown (only its last 4). */
  idproofnumber: string;
  billing: SearchAddress;
  /** By installation_id: the login's installation address. */
  installs: Record<string, SearchAddress>;
}

export interface ProfileBody {
  name?: string;
  gender?: string;
  email?: string;
  alternate_mobile?: string;
  idprooftype?: string;
  idproofnumber?: string;
  billing_address?: SearchAddress;
  installations?: ({ location_id: string } & SearchAddress)[];
}

const EMPTY_ADDRESS: SearchAddress = { address: "", city: "", state: "", pin: "" };

/** The form as the match stands: what Edit profile opens with. */
export function profileForm(m: SubscriberMatch): ProfileForm {
  const installs: Record<string, SearchAddress> = {};
  for (const l of m.logins) if (l.installation_id && l.installation_address) installs[l.installation_id] = { ...l.installation_address };
  return {
    name: m.name,
    gender: m.gender === "male" || m.gender === "female" ? m.gender : "",
    email: m.email,
    alternate_mobile: m.alternate_mobile ?? "",
    idprooftype: "",
    idproofnumber: "",
    billing: m.billing_address ? { ...m.billing_address } : { ...EMPTY_ADDRESS },
    installs,
  };
}

const t = (s: string) => s.trim();
const sameAddress = (a: SearchAddress, b: SearchAddress) => t(a.address) === t(b.address) && t(a.city) === t(b.city) && t(a.state) === t(b.state) && t(a.pin) === t(b.pin);
const blankAddress = (a: SearchAddress) => !t(a.address) && !t(a.city) && !t(a.state) && !t(a.pin);
const trimAddress = (a: SearchAddress): SearchAddress => ({ address: t(a.address), city: t(a.city), state: t(a.state), pin: t(a.pin) });

/** Only what changed from the match; {} when nothing did. */
export function profileBody(m: SubscriberMatch, f: ProfileForm): ProfileBody {
  const was = profileForm(m);
  const out: ProfileBody = {};
  if (t(f.name) !== t(was.name)) out.name = t(f.name);
  if (f.gender && f.gender !== was.gender) out.gender = f.gender;
  if (t(f.email).toLowerCase() !== t(was.email).toLowerCase()) out.email = t(f.email);
  if (t(f.alternate_mobile) && t(f.alternate_mobile) !== t(was.alternate_mobile)) out.alternate_mobile = t(f.alternate_mobile);
  if (t(f.idproofnumber)) {
    out.idprooftype = t(f.idprooftype) || m.idprooftype;
    out.idproofnumber = t(f.idproofnumber);
  }
  // A billing address the subscriber never had stays absent until it is filled in.
  if (!sameAddress(f.billing, was.billing) && !(blankAddress(f.billing) && !m.billing_address)) out.billing_address = trimAddress(f.billing);
  const inst = Object.entries(f.installs)
    .filter(([id, a]) => was.installs[id] && !sameAddress(a, was.installs[id]!))
    .map(([id, a]) => ({ location_id: id, ...trimAddress(a) }));
  if (inst.length) out.installations = inst;
  return out;
}

export function hasChanges(b: ProfileBody): boolean {
  return Object.keys(b).length > 0;
}

function addressErrors(a: SearchAddress, prefix: string, out: Record<string, string>) {
  if (!t(a.address)) out[`${prefix}.address`] = "Enter the address";
  if (!t(a.city)) out[`${prefix}.city`] = "Enter the city";
  if (!t(a.state)) out[`${prefix}.state`] = "Enter the state";
  if (!/^[1-9]\d{5}$/.test(t(a.pin))) out[`${prefix}.pin`] = "6 digits, not starting with 0";
}

/**
 * Field errors for what the body would send, keyed like the form (name,
 * gender, email, alternate_mobile, idprooftype, idproofnumber, billing.<f>,
 * install.<id>.<f>): the server's own checks, said on the field.
 */
export function profileFieldErrors(m: SubscriberMatch, f: ProfileForm): Record<string, string> {
  const b = profileBody(m, f);
  const out: Record<string, string> = {};
  if (b.name !== undefined) {
    if (!b.name) out.name = "Enter the name";
    else if (b.name.length > 100) out.name = "At most 100 characters";
    else if (!NAME_CHARS.test(b.name)) out.name = `Not allowed in a name: ${badNameChars(b.name)}`;
  }
  if (b.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(b.email)) out.email = "Not an email address";
  if (b.alternate_mobile !== undefined) {
    if (!isMobile10(b.alternate_mobile)) out.alternate_mobile = "10 digits";
    else if (b.alternate_mobile === m.mobile) out.alternate_mobile = "The same as the primary mobile";
  }
  if (b.idproofnumber !== undefined && !b.idprooftype) out.idprooftype = "Choose the ID proof type";
  if (b.billing_address) addressErrors(b.billing_address, "billing", out);
  for (const l of b.installations ?? []) addressErrors(l, `install.${l.location_id}`, out);
  return out;
}

export interface ProfileSaved {
  installations_done: string[];
}

export async function saveProfile(identity: CallerIdentity, ns: string, accountId: string, body: ProfileBody): Promise<ProfileSaved> {
  const r = await callGateway<ProfileSaved>(identity, config().gatewayBase, `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/profile`, {
    method: "PUT",
    body,
  });
  return { installations_done: r.data?.installations_done ?? [] };
}
